import i18n from "../i18n";
import React, { useState, useEffect } from "react";
import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "./pageList";
import { useFormRender } from "../hooks/useFormRender";
import { useTranslation } from "react-i18next";
import {
  Button,
  TextInput,
  Switch,
  Select,
  Textarea,
  NumberInput,
  Group,
  Text,
  Input,
  Badge,
} from "@mantine/core";
import FormInlineActionLink from "./formInlineActionLink";
import {
  IconCircle,
  IconCirclePlus,
  IconExclamationMark,
  IconSettings,
} from "@tabler/icons";

import moment from "moment";
import DummyWidget from "./dummyWidget";

import { DATA_BANK_WITH_CODE } from "../data/options";

import { DatePicker } from "@mantine/dates";
import FileList from "./fileList";

import { useServerApi } from "../hooks/userServerApi";
import { showNotification } from "@mantine/notifications";
import _ from "lodash";
import { reloadFormWithGuard } from "../helpers/formFieldSave";
import RemoteSelect from "../components/remoteSelect";
import RemoteSelect2 from "../components/remoteSelect2";
import { DATA_ORDER_TYPE, DATA_COUNTRY } from "../data/options";
import Chips from "./chips";

import FormConfirmation from "./formConfirmation";
import RemarkList from "./remarkList";
import ReactJson from "react-json-view";
import { useFormatter } from "../hooks/useFomatter";
import DebitNotePaymentMatchingList from "./debitNotePaymentMatchingList";
import { useAuthUser } from "react-auth-kit";
import { forwardRef, useImperativeHandle } from "react";

const UseClientCreditButton = forwardRef(({ mainForm, onUseCredit }, ref) => {
  const [api] = useServerApi();
  const apiEntity = "clientPayment";
  const [formatter] = useFormatter();

  const [availableCredit, setAvailableCredit] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(0);
  const initialValues = {};
  const auth = useAuthUser();
  const dn = mainForm?.values;
  const clientId = dn?.client?._id;

  const fetchCredit = async () => {
    try {
      if (!clientId) return;
      const credit = await api.Client.getAvailableCredit(clientId);
      setAvailableCredit(credit.balance);

      setRemainingAmount(
        _.round(dn?.clientPayable - (dn?.payment?.amount ?? 0), 2)
      );
    } catch (error) {
      showNotification({
        color: "red",
        icon: <IconExclamationMark size={18} />,
        title: `Service add error`,
        message: error.msg ?? error.message,
      });
    }
  };

  useImperativeHandle(ref, () => ({
    fetchCredit,
  }));

  useEffect(() => {
    fetchCredit();
  }, [clientId]);

  const handleUseCredit = () => {
    // console.log("handleUseCredit");
    if (remainingAmount <= 0) {
      window.alert("Remaining amount is less than 0");
      return;
    }

    //set the form payment amount to remaining amount
    if (availableCredit < remainingAmount) {
      formAction.setFieldValue("amount", availableCredit);
      return;
    }

    if (availableCredit >= remainingAmount) {
      formAction.setFieldValue("amount", remainingAmount);
      return;
    }
  };

  const formSchema = {
    name: "AddClientPaymentSchema",
    title: "New Client Payment",
    description: "Please follow the step to create a New Client Payment",

    display: {
      mode: ENUM_FORM_DISPLAY.MODAL,
      size: "1000px", //xs, sm, xl  ....100%
    },

    apiEntity,
    initialValues,

    validate: {
      amount: (value) => {
        if (!value) return "Payment Amount is required";
        if (value <= 0) return "Payment Amount must be greater than 0";
        return null;
      },
    },
    showSaveBar: true,

    layout: {
      containers: [
        {
          key: "t1",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
          props: {
            variant: "default", //default | pills | outline
            defaultValue: "Payment Info",
          },
          tab: [
            {
              key: "t1",
              label: "Payment Info",
              //icon: IconSettings,
            },
            {
              key: "tab-multiple",
              label: "Multiple Debit Notes",
              // icon: IconSettings,
            },
          ],
        },
      ],

      fields: [
        {
          name: "availableCredit",
          component: DummyWidget,
          parent: "t1",
          props: {
            label: "Available Credit",
            dummyRender: (v) => (
              <>
                <Group>
                  <>
                    <Input.Wrapper label="Available Credit" />
                    <Text color="green" size="md">
                      {formatter.currencyFormat(availableCredit)}
                    </Text>
                  </>
                  <>
                    <Input.Wrapper label="Remaining Amount" />
                    <Text color="red" size="md">
                      {formatter.currencyFormat(remainingAmount)}
                    </Text>
                  </>
                  <Button size="xs" onClick={() => handleUseCredit()}>{i18n.t("Use Credit")}</Button>
                </Group>
              </>
            ),
          },
        },

        //Payment Amount
        {
          name: "amount",
          component: NumberInput,
          parent: "t1",
          props: {
            label: "Payment Amount",
            //parse as money
            parser: formatter.currencyParser,
            formatter: formatter.currencyFormat,
            precision: 2,
          },
        },

        {
          name: "remark",
          parent: "t1",
          component: Textarea,
          props: {
            label: "Remark",
          },
        },
        // {
        //   name: "isMultiple",
        //   parent: "section-debit-note",
        //   component: Switch,
        //   props: {
        //     label: "Pay to multiple debit notes",
        //   },
        // },
        {
          name: "debitNotes",
          parent: "tab-multiple",
          component: DebitNotePaymentMatchingList,
          props: {
            label: "Debit Notes",
            clientId,
          },
        },
      ],
    },
  };

  formSchema.title = "Add Payment By Client Credit";

  const onSubmit = async ({ values, formMode }) => {
    try {
      if (!values.date) {
        values.date = moment().format("YYYY-MM-DD");
      }
      const totalAssignedAmount = +_.sumBy(
        values.debitNoteAssignments,
        "paymentAmount"
      ).toFixed(2);

      if (values.amount <= 0) {
        window.alert("Payment Amount must be greater than 0");
        return;
      }
      if (Math.abs(values.amount - totalAssignedAmount) > 0.01) {
        window.alert(
          `Payment Amount (${values.amount}) must be equal to the total assigned amount (${totalAssignedAmount})`
        );
        return;
      }

      values.type = "CREDIT";
      values.status = "MATCHED";
      const data = await api.ClientPayment.add({
        payment: values,
        debitNote: mainForm?.values._id,
      });

      // console.log("onSubmit data", data);

      formAction.close();
      if (onUseCredit) onUseCredit(data);

      const cp = data?.clientPayment ?? data ?? {};
      const paymentCode = cp.code ?? "";
      const paymentAmount = cp.amount != null ? `HKD ${Number(cp.amount).toLocaleString("en-HK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "";
      const paymentMethod = cp.paymentMethod ?? "CLIENT_CREDIT";
      const paymentDate = cp.date ? new Date(cp.date).toLocaleDateString("en-HK", { day: "2-digit", month: "short", year: "numeric" }) : "";
      const paymentRemark = cp.remark ?? "";

      const paymentChanges = [
        paymentCode    && { fieldLabel: "Code",    oldValue: "", newValue: paymentCode },
        paymentAmount  && { fieldLabel: "Amount",  oldValue: "", newValue: paymentAmount },
        paymentMethod  && { fieldLabel: "Method",  oldValue: "", newValue: paymentMethod },
        paymentDate    && { fieldLabel: "Date",    oldValue: "", newValue: paymentDate },
        paymentRemark  && { fieldLabel: "Remark",  oldValue: "", newValue: paymentRemark },
      ].filter(Boolean);

      api.addActivityLog({
        entityId: mainForm?.values._id,
        title: `Payment Added${paymentCode ? ` [${paymentCode}]` : ""}`,
        by: auth().id,
        description: [paymentAmount, paymentMethod, paymentDate].filter(Boolean).join(" | "),
        changes: paymentChanges,
      });

      reloadFormWithGuard(mainForm);
      // console.log("data", data);
      fetchCredit();

      showNotification({
        title: `Payment Added`,
        message: "payment added succesfully",
      });
    } catch (error) {
      showNotification({
        color: "red",
        icon: <IconExclamationMark size={18} />,
        title: `Service add error`,
        message: error.msg ?? error.message,
      });
    }
  };

  const [renderForm, formAction, formStatus, formSetting] = useFormRender(
    formSchema,
    null,
    onSubmit
  );

  const handleActionClick = () => {
    if (!mainForm || !mainForm.values) return;

    if (
      // mainForm.values.paymentStatus === "PAID" ||
      mainForm.values.paymentStatus === "SETTLED"
    ) {
      showNotification({
        color: "red",
        icon: <IconExclamationMark size={18} />,
        title: "Error",
        message: `Payment for ${mainForm.values.code} is already ${mainForm.values.paymentStatus}, cannot add new payment`,
      });
      return;
    }

    const id = mainForm?.values._id;
    if (!id) return;

    const v = {
      date: null,
      amount: 0,
      bankAccount: null,
      remark: "",
      files: [],
      isMultiple: false,
      debitNoteAssignments: [
        {
          debitNote: mainForm.values,
          paymentAmount: mainForm.values.clientPayable,
        },
      ],
    };

    formAction.open({ mode: "edit", id, data: v });
  };

  const { t } = useTranslation();
  return (
    <>
      {availableCredit > 0 && (
        <FormInlineActionLink
          onClick={handleActionClick}
          disabled={remainingAmount <= 0 || availableCredit <= 0}
          icon={IconCirclePlus}
          label={t("Use Credit")}
        >
          <Group spacing={2}>
            <Badge color="green" size="xs" radius={0}>
              {"Available"}
            </Badge>
            <Text size={"xs"}>
              {formatter.currencyFormat(availableCredit)}
            </Text>
          </Group>
        </FormInlineActionLink>
      )}
      {renderForm()}
    </>
  );
});

export default UseClientCreditButton;
